import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Upload, 
  FileXls, 
  CheckCircle, 
  XCircle, 
  Plus,
  CloudArrowUp
} from '@phosphor-icons/react';
import { studentsApi, programsApi, formatApiErrorDetail } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const studentSchema = z.object({
  registrationNumber: z.string().min(1, 'Registration number is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  studentName: z.string().min(1, 'Student name is required'),
  programCode: z.string().min(1, 'Program is required'),
});

export default function UploadStudentsPage() {
  const [programs, setPrograms] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [file, setFile] = useState(null);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(studentSchema),
  });

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      const { data } = await programsApi.getAll();
      setPrograms(data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const onManualSubmit = async (data) => {
    setUploading(true);
    try {
      // Ensure date is in yyyy-MM-dd format
      const formattedData = {
        ...data,
        dateOfBirth: data.dateOfBirth // HTML date input already returns yyyy-MM-dd
      };
      await studentsApi.create(formattedData);
      toast.success('Student added successfully');
      reset();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const { data } = await studentsApi.bulkUpload(file);
      setUploadResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} students uploaded successfully`);
      }
      setFile(null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <p className="overline mb-2">Student Management</p>
        <h1 className="text-3xl font-black tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Upload Students
        </h1>
      </div>

      <Tabs defaultValue="manual" className="space-y-6">
        <TabsList className="bg-[#FEF7F7] p-1 rounded-none">
          <TabsTrigger 
            value="manual" 
            className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-manual"
          >
            Manual Entry
          </TabsTrigger>
          <TabsTrigger 
            value="bulk" 
            className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-bulk"
          >
            Bulk Upload
          </TabsTrigger>
        </TabsList>

        {/* Manual Entry */}
        <TabsContent value="manual">
          <div className="bg-white border border-[#E8E8E8] p-6">
            <h2 className="text-lg font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Add Single Student
            </h2>

            <form onSubmit={handleSubmit(onManualSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-[#1A1A1A]">Registration Number</Label>
                  <Input
                    {...register('registrationNumber')}
                    data-testid="student-reg-input"
                    className="mt-2 rounded-none"
                    placeholder="e.g., REG2021001"
                  />
                  {errors.registrationNumber && (
                    <p className="text-[#E31E25] text-sm mt-1">{errors.registrationNumber.message}</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#1A1A1A]">Student Name</Label>
                  <Input
                    {...register('studentName')}
                    data-testid="student-name-input"
                    className="mt-2 rounded-none"
                    placeholder="Full name"
                  />
                  {errors.studentName && (
                    <p className="text-[#E31E25] text-sm mt-1">{errors.studentName.message}</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#1A1A1A]">Date of Birth</Label>
                  <Input
                    type="date"
                    {...register('dateOfBirth')}
                    data-testid="student-dob-input"
                    className="mt-2 rounded-none"
                    max="2010-12-31"
                    min="1980-01-01"
                  />
                  <p className="text-xs text-[#666666] mt-1">Select date from calendar</p>
                  {errors.dateOfBirth && (
                    <p className="text-[#E31E25] text-sm mt-1">{errors.dateOfBirth.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-[#1A1A1A]">Program</Label>
                  <Select onValueChange={(value) => setValue('programCode', value)}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="student-program-select">
                      <SelectValue placeholder="Select program" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {programs.map((p) => (
                        <SelectItem key={p.programCode} value={p.programCode}>
                          {p.programName} ({p.programCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.programCode && (
                    <p className="text-[#E31E25] text-sm mt-1">{errors.programCode.message}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={uploading}
                data-testid="add-student-btn"
                className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none"
              >
                {uploading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Adding...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Plus size={18} weight="bold" />
                    Add Student
                  </div>
                )}
              </Button>
            </form>
          </div>
        </TabsContent>

        {/* Bulk Upload */}
        <TabsContent value="bulk">
          <div className="bg-white border border-[#E8E8E8] p-6">
            <h2 className="text-lg font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Bulk Upload via Excel/CSV
            </h2>

            {/* File Format Info */}
            <div className="bg-[#FEF7F7] p-4 mb-6">
              <h3 className="font-semibold text-sm text-[#1A1A1A] mb-2">Required Columns:</h3>
              <div className="flex flex-wrap gap-2">
                {['Registration Number', 'Date of Birth', 'Student Name', 'Program Code'].map((col) => (
                  <span key={col} className="bg-white px-2 py-1 text-xs font-medium border border-[#E8E8E8]">
                    {col}
                  </span>
                ))}
              </div>
            </div>

            {/* Dropzone */}
            <div
              {...getRootProps()}
              data-testid="student-dropzone"
              className={`dropzone ${isDragActive ? 'active' : ''}`}
            >
              <input {...getInputProps()} />
              <CloudArrowUp size={48} weight="duotone" className="mx-auto mb-4 text-[#666666]" />
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileXls size={24} className="text-[#00A859]" />
                  <span className="font-medium">{file.name}</span>
                </div>
              ) : (
                <>
                  <p className="text-[#1A1A1A] font-medium">
                    Drag & drop your Excel/CSV file here
                  </p>
                  <p className="text-sm text-[#666666] mt-1">
                    or click to browse
                  </p>
                </>
              )}
            </div>

            {file && (
              <div className="mt-6 flex gap-4">
                <Button
                  onClick={handleBulkUpload}
                  disabled={uploading}
                  data-testid="upload-students-btn"
                  className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none"
                >
                  {uploading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload size={18} weight="bold" />
                      Upload Students
                    </div>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setUploadResult(null);
                  }}
                  className="rounded-none"
                >
                  Clear
                </Button>
              </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <div className="mt-6 p-4 border border-[#E8E8E8]">
                <div className="flex items-center gap-2 mb-3">
                  {uploadResult.inserted > 0 ? (
                    <CheckCircle size={24} className="text-[#00A859]" weight="duotone" />
                  ) : (
                    <XCircle size={24} className="text-[#E31E25]" weight="duotone" />
                  )}
                  <span className="font-semibold text-[#1A1A1A]">{uploadResult.message}</span>
                </div>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-[#E31E25]/5 border border-[#E31E25]/20">
                    <p className="text-sm font-medium text-[#E31E25] mb-2">Errors:</p>
                    <ul className="text-sm text-[#666666] space-y-1">
                      {uploadResult.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
